import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  CopyButton,
  Divider,
  Group,
  Modal,
  MultiSelect,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconBolt,
  IconCheck,
  IconCode,
  IconCopy,
  IconExternalLink,
  IconKey,
  IconPlugConnected,
  IconRefresh,
  IconSend,
  IconTrash,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { trpc } from '../../lib/trpc';
import { PermissionGate } from '../../components/PermissionGate';

const EVENT_TYPES = [
  { value: 'message.received', label: 'Message received (inbound)' },
  { value: 'message.sent', label: 'Message sent (outbound)' },
  { value: 'message.status', label: 'Message status (delivered/read/failed)' },
  { value: 'contact.created', label: 'Contact created' },
  { value: 'bot_reply.fired', label: 'Bot reply fired' },
  { value: 'flow.response', label: 'Form response submitted' },
];

const SAMPLE_PAYLOAD = `{
  "contact": { "phone_number": "14155550100", "first_name": "Ada", "uid": "..." },
  "message": {
    "message_id": "wamid....",
    "type": "text",
    "text": "Hello!",
    "status": "received",
    "timestamp": "2026-06-14T10:30:00Z"
  }
}`;

function ApiDeveloperPage() {
  const utils = trpc.useUtils();
  const access = trpc.tenancy.apiAccess.get.useQuery();
  const subs = trpc.tenancy.outgoingWebhook.subscriptions.useQuery();

  const [revealToken, setRevealToken] = useState(false);
  const [createOpen, ch] = useDisclosure(false);
  const [targetUrl, setTargetUrl] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const [newSecret, setNewSecret] = useState<string | null>(null);

  const rotate = trpc.tenancy.apiAccess.regenerate.useMutation({
    onSuccess: () => {
      void utils.tenancy.apiAccess.get.invalidate();
      notifications.show({ message: 'API key rotated — update your connectors', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const createSub = trpc.tenancy.outgoingWebhook.createSubscription.useMutation({
    onSuccess: (r) => {
      void utils.tenancy.outgoingWebhook.subscriptions.invalidate();
      setNewSecret(r.secret);
      setTargetUrl('');
      setEvents([]);
      ch.close();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const revoke = trpc.tenancy.outgoingWebhook.revokeSubscription.useMutation({
    onSuccess: () => void utils.tenancy.outgoingWebhook.subscriptions.invalidate(),
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const testEvent = trpc.tenancy.outgoingWebhook.testEvent.useMutation({
    onSuccess: () =>
      notifications.show({
        message: 'Test event sent to all active subscriptions',
        color: 'green',
      }),
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const token = access.data?.token ?? '';
  const maskedToken = token ? `${'•'.repeat(56)}${token.slice(-8)}` : '';
  const hookRows = subs.data ?? [];

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="sm">
            <IconCode size={22} />
            <Title order={2}>API &amp; Developer</Title>
          </Group>
          <Button
            variant="default"
            leftSection={<IconExternalLink size={14} />}
            component="a"
            href="/docs"
            target="_blank"
          >
            API docs
          </Button>
        </Group>
        <Text c="dimmed" size="sm">
          Connect LeadSync to Zapier, Make, Pabbly, or your own code. Use the API key below to
          authenticate, subscribe to events (triggers), and call actions like sending a message.
        </Text>

        {/* ── API access ───────────────────────────────────────────── */}
        <Card withBorder>
          <Stack gap="sm">
            <Group gap="xs">
              <IconKey size={18} />
              <Title order={5}>API access</Title>
            </Group>
            {access.isPending ? (
              <Skeleton height={120} />
            ) : (
              <>
                <ReadonlyField label="Vendor UID" value={access.data?.vendorUid ?? ''} />
                <ReadonlyField label="API base URL" value={access.data?.baseUrl ?? ''} />
                <Group align="flex-end" wrap="nowrap">
                  <TextInput
                    flex={1}
                    label="API key (Bearer token)"
                    readOnly
                    value={revealToken ? token : maskedToken}
                    ff="monospace"
                  />
                  <Button variant="default" onClick={() => setRevealToken((v) => !v)} mb={1}>
                    {revealToken ? 'Hide' : 'Show'}
                  </Button>
                  <CopyButton value={token}>
                    {({ copy, copied }) => (
                      <Tooltip label={copied ? 'Copied' : 'Copy'}>
                        <ActionIcon variant="default" size="lg" onClick={copy} mb={1}>
                          {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Group>
                <Group>
                  <Button
                    variant="light"
                    color="orange"
                    leftSection={<IconRefresh size={14} />}
                    loading={rotate.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          'Rotate the API key? Existing connectors using the old key will stop working until updated.',
                        )
                      )
                        rotate.mutate({});
                    }}
                  >
                    Rotate key
                  </Button>
                  <Text size="xs" c="dimmed">
                    Treat this key like a password. Rotating invalidates the previous key
                    immediately.
                  </Text>
                </Group>
              </>
            )}
          </Stack>
        </Card>

        {/* ── REST hooks ───────────────────────────────────────────── */}
        <Card withBorder>
          <Stack gap="sm">
            <Group justify="space-between">
              <Group gap="xs">
                <IconBolt size={18} />
                <Title order={5}>Webhook subscriptions (triggers)</Title>
              </Group>
              <Group gap="xs">
                <Button
                  variant="default"
                  size="xs"
                  leftSection={<IconSend size={14} />}
                  loading={testEvent.isPending}
                  onClick={() => testEvent.mutate({})}
                >
                  Send test event
                </Button>
                <Button size="xs" leftSection={<IconPlugConnected size={14} />} onClick={ch.open}>
                  Add subscription
                </Button>
              </Group>
            </Group>
            <Text size="xs" c="dimmed">
              Each subscription POSTs the selected events to your URL, signed with{' '}
              <Code>X-LeadSync-Signature</Code>. Zapier/Make create these automatically; you can
              also add one manually for custom code.
            </Text>
            {subs.isPending ? (
              <Skeleton height={80} />
            ) : hookRows.length === 0 ? (
              <Text c="dimmed" size="sm" py="xs">
                No subscriptions yet.
              </Text>
            ) : (
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>URL</Table.Th>
                    <Table.Th>Events</Table.Th>
                    <Table.Th>Source</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {hookRows.map((s) => (
                    <Table.Tr key={s.uid}>
                      <Table.Td>
                        <Text size="xs" ff="monospace" style={{ wordBreak: 'break-all' }}>
                          {s.targetUrl}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">{s.events.length ? s.events.join(', ') : 'all'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="xs" variant="light" tt="capitalize">
                          {s.source}
                        </Badge>
                      </Table.Td>
                      <Table.Td align="right">
                        <Tooltip label="Revoke">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            loading={revoke.isPending && revoke.variables?.uid === s.uid}
                            onClick={() => revoke.mutate({ uid: s.uid })}
                          >
                            <IconTrash size={15} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        </Card>

        {/* ── Connect guides ───────────────────────────────────────── */}
        <Card withBorder>
          <Stack gap="sm">
            <Group gap="xs">
              <IconPlugConnected size={18} />
              <Title order={5}>Connect a no-code tool</Title>
            </Group>
            <Group grow align="stretch">
              <ConnectCard
                name="Zapier"
                href="https://zapier.com/apps/leadsync/integrations"
                steps={[
                  'Search "LeadSync" in Zapier and add the app.',
                  'Paste your Vendor UID + API key when prompted.',
                  'Pick a trigger (e.g. New message) or action (Send template).',
                ]}
              />
              <ConnectCard
                name="Make"
                href="https://www.make.com/en/integrations"
                steps={[
                  'Add an "HTTP" / custom app module.',
                  'Use the API base URL + Bearer key above.',
                  'Subscribe via POST /hooks, or call actions directly.',
                ]}
              />
              <ConnectCard
                name="Pabbly"
                href="https://www.pabbly.com/connect/integrations/"
                steps={[
                  'Add a Webhook / API action.',
                  'Use the API base URL + Bearer key above.',
                  'Use POST /hooks for triggers, action endpoints for sends.',
                ]}
              />
            </Group>
          </Stack>
        </Card>

        {/* ── Event reference ──────────────────────────────────────── */}
        <Card withBorder>
          <Stack gap="sm">
            <Title order={5}>Event reference</Title>
            <Text size="xs" c="dimmed">
              Events you can subscribe to as triggers. See the full request/response shapes in the{' '}
              <Anchor href="/docs" target="_blank">
                API docs
              </Anchor>
              .
            </Text>
            <Group gap={6}>
              {EVENT_TYPES.map((e) => (
                <Badge key={e.value} variant="light">
                  {e.value}
                </Badge>
              ))}
            </Group>
            <Text size="xs" c="dimmed" mt="xs">
              Sample <Code>message.received</Code> payload:
            </Text>
            <Code block>{SAMPLE_PAYLOAD}</Code>
          </Stack>
        </Card>
      </Stack>

      {/* Create subscription modal */}
      <Modal opened={createOpen} onClose={ch.close} title="Add webhook subscription" centered>
        <Stack>
          <TextInput
            label="Target URL"
            placeholder="https://hooks.zapier.com/..."
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.currentTarget.value)}
          />
          <MultiSelect
            label="Events"
            description="Leave empty to receive all events."
            data={EVENT_TYPES}
            value={events}
            onChange={setEvents}
            searchable
          />
          <Group justify="end">
            <Button variant="default" onClick={ch.close}>
              Cancel
            </Button>
            <Button
              loading={createSub.isPending}
              disabled={!/^https?:\/\//.test(targetUrl)}
              onClick={() => createSub.mutate({ targetUrl: targetUrl.trim(), events })}
            >
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* One-time secret reveal */}
      <Modal
        opened={!!newSecret}
        onClose={() => setNewSecret(null)}
        title="Subscription created"
        centered
      >
        <Stack>
          <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
            Copy this signing secret now — it won&apos;t be shown again. Use it to verify the{' '}
            <Code>X-LeadSync-Signature</Code> header on incoming deliveries.
          </Alert>
          <Group align="flex-end" wrap="nowrap">
            <TextInput flex={1} readOnly value={newSecret ?? ''} ff="monospace" />
            <CopyButton value={newSecret ?? ''}>
              {({ copy, copied }) => (
                <Button variant="default" onClick={copy} mb={1}>
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              )}
            </CopyButton>
          </Group>
          <Group justify="end">
            <Button onClick={() => setNewSecret(null)}>Done</Button>
          </Group>
        </Stack>
      </Modal>
    </motion.div>
  );
}

function ReadonlyField({ label, value }: { label: string; value: string }) {
  return (
    <Group align="flex-end" wrap="nowrap">
      <TextInput flex={1} label={label} readOnly value={value} ff="monospace" />
      <CopyButton value={value}>
        {({ copy, copied }) => (
          <Tooltip label={copied ? 'Copied' : 'Copy'}>
            <ActionIcon variant="default" size="lg" onClick={copy} mb={1}>
              {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  );
}

function ConnectCard({ name, href, steps }: { name: string; href: string; steps: string[] }) {
  return (
    <Card withBorder padding="sm">
      <Stack gap="xs">
        <Group justify="space-between">
          <Text fw={600}>{name}</Text>
          <Anchor href={href} target="_blank" size="xs">
            Open <IconExternalLink size={11} style={{ verticalAlign: 'middle' }} />
          </Anchor>
        </Group>
        <Divider />
        <Stack gap={4}>
          {steps.map((s, i) => (
            <Text key={i} size="xs" c="dimmed">
              {i + 1}. {s}
            </Text>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}

function ApiDeveloperGated() {
  return (
    <PermissionGate permission="manage_integrations">
      <ApiDeveloperPage />
    </PermissionGate>
  );
}

export const Route = createFileRoute('/vendor/integrations/api')({ component: ApiDeveloperGated });
